import React, { useEffect, useMemo, useRef, useState } from "react";
import { restante } from "../../../acuerdos/cuentaRegresiva";
import { Modal } from "react-bootstrap";

import HttpManager from "../../../../services/HttpManager";
import { hoyCdmx } from "../../../../utils/horaMexico";
import useEsTelefono from "../../../../utils/esTelefono";
import { abrirArchivo } from "../../../enrollment/visorArchivo";
import { cursoCorto } from "../../../../utils/cursoCorto";
import { horarioBonito } from "../../../../utils/horarioBonito";
import R from "./strings";
import { useUnaALaVez } from "../../../../utils/unaALaVez";

/*
	"Registrar pago" (docs/ESPEC_COBROS_UNIFICADO.md, Etapa 1): el MISMO modal en Pagos por Procesar (desde el
	ticket, con el comprobante a la izquierda), en Pagos › Nuevo pago (en blanco) y en Finanzas del alumno (con el
	alumno puesto). Reglas que la pantalla solo ayuda a cumplir (el servidor las vuelve a validar todas):
	- Caja decide a qué va cada peso: "Por asignar" en oro y el botón apagado hasta que Recibido − Aplicado = 0.
	- Lo sugerido solo marca lo que el monto cubre COMPLETO, vencido primero; el sobrante lo asigna la cajera.
	- Cada renglón con su "Se aplica" (abono en cualquier renglón); en $0 = saldado.
	- Descuentos: promo del catálogo (sobre precio de lista) o especial pedido a Supervisión de Caja.
*/

export interface RegisterStudent { id: number; name?: string; firstName?: string; lastName?: string; identityCode?: string | null; }

export interface RegisterPaymentModalProps {
	campusId: number;
	currencySymbol: string;
	/** Finanzas del alumno: ya viene puesto. */
	initialStudent?: RegisterStudent | null;
	/** Desde un ticket de Pagos por Procesar: el detalle del ticket (prellenado, comprobantes, borrador). */
	ticket?: any | null;
	onClose: () => void;
	/** Al terminar (después de enseñar el resultado): el detalle del pago registrado. */
	onSuccess: (receipt: any) => void;
}

interface Promo { id: number; campusId: number; code: string; name: string; group: string; appliesTo: string; calcType: string; value: number; rules: any; conditions: string; validTo: string | null;
	/** Promos flexibles: fin de la cuenta regresiva (ISO con zona del plantel), cupo y si está agotada. */
	countdownToIso?: string | null; quota?: any; exhausted?: boolean;
	/** Diferencia reloj del servidor − reloj del teléfono al cargar (ms): la cuenta regresiva se mide con la hora del servidor. */
	serverNowIso?: string | null; offsetMs?: number; }
interface DReq { id: number; statusId: number; status: string; calcType: string; value: number; listAmount: number; discountAmount: number; reason: string; decisionNotes: string | null; rowKey: string; }
interface Row {
	key: string; kind: "tuition" | "article"; id: number; campusId: number; campusName: string; statusId: number;
	listAmount: number; subTotal: number; paidAmount: number; pending: number; currentDiscount: number;
	invoiceId: number | null; overdue: boolean; canDiscount: boolean; enrollmentId: number | null; typeId: number | null;
	monthIndex: number | null; monthsTotal: number | null; courseName: string; levelName: string; scheduleName: string;
	courseId?: number | null; levelId?: number | null; periodId?: number | null;
	periodStart: string | null; dueDate: string | null; isTicket: boolean; title: string; discountRequest: DReq | null;
	/** Recargo automático (Etapa 2): el recargo va dentro del renglón de su mensualidad (parentKey / surchargeKey). */
	surcharge: any | null; parentKey: string | null; surchargeKey: string | null; discountBlocked: string | null;
	/** Acuerdo comercial (Etapa 3): el precio ya lo puso el motor; agreementLock = la matrícula tiene acuerdo (no se combina otra promo). */
	agreement?: { id: number; name: string; status: string; state: string; listAmount: number; target: number; applied: string; note: string | null; conditionMet: boolean; conditionText: string | null } | null;
	agreementLock?: string | null;
	/** Mes regalado pendiente de pago adelantado: las mensualidades ("t123") que tienen que pagarse completas (de una vez) para que quede en $0. */
	conditionKeys?: string[] | null;
}
interface Line { on: boolean; apply: string; touched: boolean; promotionId: number | null; drId: number | null; note: string; }
/** A2': lo que falta adelantar por acuerdo pendiente (lista × N − lo ya pagado) y qué meses quedan en $0 al registrarlo. */
interface Advance { agreementId: number; name: string; type: string; text: string | null; amount: number; keys: string[]; pendingCount: number; gifts: { tuitionId: number; title: string; statusId: number; subTotal: number; studentId: number }[]; giftStudentId: number; ownerEnrollmentId: number; }
/** Multimes / 2x1 que ya no puede cumplirse (plazo vencido…): una supervisora puede «Aprobar de todos modos» (ESPEC_AUDITORIA_PREGUNTAS B). */
interface BlockedAdvance { agreementId: number; name: string; text: string; canApprove: boolean; }
interface Person { student: RegisterStudent; campusNames: string[]; loading: boolean; rows: Row[]; warnings: string[]; promos: Promo[]; lines: Record<string, Line>; notes: string; canWaive?: boolean; advances: Advance[]; blocked?: BlockedAdvance[]; }

const DR_PENDIENTE = 1, DR_ACEPTADO = 2, DR_RECHAZADO = 3;
const round2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const money = (n: number, s: string) => s + round2(n).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const nameOf = (s: RegisterStudent) => (s.name || [s.firstName, s.lastName].filter(Boolean).join(" ")).trim();
const newKey = () => ((window as any).crypto && (window as any).crypto.randomUUID ? (window as any).crypto.randomUUID() : String(Date.now()) + "-" + Math.random().toString(16).slice(2));
const plain = (s: string) => (s || "").toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");
const LB: React.CSSProperties = { position: "static", display: "block", margin: 0, whiteSpace: "normal", overflow: "visible" };
const fecha = (d: string | null) => d ? d.slice(8, 10) + "/" + d.slice(5, 7) + "/" + d.slice(0, 4) : "—";

/** El mensaje del servidor tal cual (409 trae "error", 422/400 traen "errors"). */
export const serverError = (e: any): string => {
	const d = e?.response?.data;
	if (d) {
		if (d.errors && typeof d.errors === "object") {
			const parts: string[] = [];
			Object.keys(d.errors).forEach((k) => { const v = d.errors[k]; parts.push(Array.isArray(v) ? v[0] : String(v)); });
			if (parts.length) return parts.join(" ");
		}
		if (typeof d.error === "string" && d.error) return d.error;
		if (d.message) return String(d.message);
	}
	return e?.message ? String(e.message) : "Ocurrió un error inesperado.";
};

/** El mismo cálculo del servidor: SIEMPRE sobre el precio de lista; reemplaza al descuento que traiga el renglón. */
const discountFrom = (list: number, calc: string, value: number): number => {
	if (list <= 0.009) return 0;
	if (calc === "percent") return round2(list * Math.min(100, Math.max(0, value)) / 100);
	if (calc === "fixed_price") return round2(Math.max(0, list - value));
	return round2(Math.max(0, Math.min(list, value)));
};

/** Promos que pueden aplicar a este renglón (filtro rápido; el servidor revisa las reglas completas). */
const promosFor = (row: Row, promos: Promo[]): Promo[] => {
	// Con acuerdo comercial no se combina otra promo del catálogo (Etapa 3): solo el especial de Supervisión.
	if (!row.canDiscount || row.agreementLock) return [];
	const course = plain(row.courseName + " " + row.levelName);
	return promos.filter((p) => {
		if (p.campusId !== row.campusId) return false;
		const tipo = (p.appliesTo === "subscription" && row.kind === "tuition" && row.typeId === 0)
			|| (p.appliesTo === "monthly" && row.kind === "tuition" && row.typeId === 1) || (p.appliesTo === "article" && row.kind === "article");
		if (!tipo) return false;
		const r = p.rules || {};
		if (row.kind === "tuition" && Array.isArray(r.courses) && r.courses.length && !r.courses.some((k: string) => course.includes(k))) return false;
		// Promos flexibles: cursos y módulos reales; la que ya terminó (cuenta regresiva en cero) o está agotada no se ofrece.
		if (row.kind === "tuition" && ((r.courseIds || []).length || (r.levelIds || []).length)
			&& !(r.courseIds || []).includes(row.courseId) && !(r.levelIds || []).includes(row.levelId)) return false;
		if (row.kind === "tuition" && ((r.excludeCourseIds || []).includes(row.courseId) || (r.excludeLevelIds || []).includes(row.levelId))) return false;
		if (p.countdownToIso && Date.parse(p.countdownToIso) <= Date.now() + (p.offsetMs || 0)) return false;
		if (p.exhausted) return false;
		if (row.kind === "tuition" && Array.isArray(r.excludeCourses) && r.excludeCourses.some((k: string) => course.includes(k))) return false;
		if ((r.monthFrom || r.monthTo) && ((row.monthIndex || 0) < (r.monthFrom || 1) || (row.monthIndex || 0) > (r.monthTo || 999))) return false;
		if (r.monthsTotal && row.kind === "tuition" && row.monthsTotal !== r.monthsTotal) return false;
		if (r.freeLast && ((row.monthIndex || 0) <= (row.monthsTotal || 0) - r.freeLast)) return false;
		if (r.periodFrom && (!row.periodStart || row.periodStart < r.periodFrom)) return false;
		return true;
	});
};

const RegisterPaymentModal: React.FC<RegisterPaymentModalProps> = ({ campusId, currencySymbol, initialStudent, ticket, onClose, onSuccess }) => {
	const http = HttpManager.getInstance();
	const movil = useEsTelefono();
	const intentId: number | null = ticket ? Number(ticket.id) : null;
	const clientKey = useRef<string>(newKey());

	const [methods, setMethods] = useState<any[]>([]);
	const [busy, setBusy] = useState<boolean>(false);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [result, setResult] = useState<any>(null);

	const [total, setTotal] = useState<string>(ticket?.amount ? String(ticket.amount) : "");
	const [methodId, setMethodId] = useState<number>(Number(ticket?.paymentMethodId || 0));
	const [accountId, setAccountId] = useState<number>(Number(ticket?.recipientAccountId || 0));
	const [paymentDate, setPaymentDate] = useState<string>(ticket?.paidAt ? String(ticket.paidAt).slice(0, 10) : hoyCdmx());
	const [reference, setReference] = useState<string>(ticket?.reference || "");
	const [refCheck, setRefCheck] = useState<{ ok: boolean; repeated: boolean; message: string | null } | null>(null);
	const [payerName, setPayerName] = useState<string>("");
	const [payerPhone, setPayerPhone] = useState<string>("");
	const [notes, setNotes] = useState<string>(ticket ? (ticket.notes ? String(ticket.notes).split("\n")[0].slice(0, 500) : `Pago del ticket #${ticket.id}`) : "");
	const [files, setFiles] = useState<File[]>([]);

	const [persons, setPersons] = useState<Person[]>([]);
	// R3 (BAJO 9): ticket sin alumno en WAPP → la cajera marca de quién es el comprobante (por defecto, la primera persona del pago).
	const [owner, setOwner] = useState<number>(0);
	const [manual, setManual] = useState<boolean>(false);
	const [searchOpen, setSearchOpen] = useState<boolean>(!initialStudent && !ticket);
	const [query, setQuery] = useState<string>("");
	const [results, setResults] = useState<any[]>([]);
	const [searching, setSearching] = useState<boolean>(false);
	const [special, setSpecial] = useState<{ studentId: number; key: string; calcType: "percent" | "amount"; value: string; reason: string } | null>(null);
	// Eximir el recargo: Supervisión de Caja lo hace aquí mismo; la cajera lo sugiere (Cliq a la supervisora).
	// Esteban 2026-10-09: pagar tarde nunca quita el descuento, así que eximir no tiene nada que «devolver».
	const [waiver, setWaiver] = useState<{ studentId: number; key: string; surchargeId: number; mode: "waive" | "request"; calcType: "percent" | "amount"; value: string; reason: string; pending: number } | null>(null);
	const [preview, setPreview] = useState<{ url: string | null; type: string; name: string; index: number } | null>(null);
	// Lectura IA del comprobante del ticket (prellenado + avisos "Revisar"). Sin IA: campos vacíos, sin error.
	const [ai, setAi] = useState<{ loading: boolean; prefill: any; warnings: string[]; readings: any[] } | null>(ticket ? { loading: true, prefill: null, warnings: [], readings: [] } : null);
	const aiApplied = useRef<boolean>(false);
	const pendingAccount = useRef<number>(0);

	const method = useMemo(() => methods.find((m: any) => Number(m.id) === methodId) || null, [methods, methodId]);
	const accounts: any[] = method?.accounts || [];
	const ticketDocs: any[] = ticket ? (ticket.documents && ticket.documents.length ? ticket.documents : (ticket.hasDocument ? [{ index: 0, name: ticket.documentName || "Comprobante" }] : [])).filter((d: any) => !d.rejected) : [];
	const draft = ticket?.draft || null;

	// ------------------------------------------------------------------ carga

	useEffect(() => {
		http.paymentService.registerMethods(campusId, !!ticket).then((r: any) => {
			const list = r.data || [];
			setMethods(list);
			// El método que pide el borrador o el ticket, si está permitido aquí; si no, el primero de la lista.
			const wanted = Number((draft && draft.methodId) || ticket?.paymentMethodId || 0);
			// Si ya hay un método válido elegido (por la lectura del comprobante o por la cajera), no se pisa.
			setMethodId((prev) => (prev && list.some((m: any) => Number(m.id) === prev)) ? prev
				: (list.some((m: any) => Number(m.id) === wanted) ? wanted : (list.length ? Number(list[0].id) : 0)));
		}).catch((e: any) => setError(serverError(e)));
		//Un ticket sin alumno en WAPP (el que mandó el alumno por /pagar y no se encontró) no lleva persona fantasma: pedía /_owed/0 y salía "Alumno no encontrado".
		const first: RegisterStudent | null = ticket ? (Number(ticket.studentId) > 0 ? { id: Number(ticket.studentId), name: ticket.studentName, identityCode: ticket.identityCode } : null) : (initialStudent || null);
		const people: RegisterStudent[] = [];
		if (first) people.push(first);
		// Personas que se matricularon juntas (Etapa 3): el mismo ticket las cubre a todas.
		(ticket?.companions || []).forEach((c: any) => { if (!people.some((x) => x.id === Number(c.studentId))) people.push({ id: Number(c.studentId), name: c.name }); });
		if (draft && Array.isArray(draft.persons)) {
			draft.persons.forEach((p: any) => { if (!people.some((x) => x.id === p.student?.id)) people.push(p.student); });
		}
		if (draft) {
			setTotal(draft.total ?? ""); setMethodId(Number(draft.methodId || 0)); setAccountId(Number(draft.accountId || 0));
			setPaymentDate(draft.paymentDate || hoyCdmx()); setReference(draft.reference || ""); setPayerName(draft.payerName || "");
			setPayerPhone(draft.payerPhone || ""); setNotes(draft.notes || ""); setManual(true);
			setNotice(R.draftRestored(ticket.draftAt ? ticket.draftAt.replace("T", " ") : ""));
		}
		people.forEach((s) => addPerson(s, draft));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (!method) return;
		if (pendingAccount.current && accounts.some((a: any) => Number(a.id) === pendingAccount.current)) {
			setAccountId(pendingAccount.current);
			pendingAccount.current = 0;
		} else if (!accounts.some((a: any) => Number(a.id) === accountId)) setAccountId(accounts.length === 1 ? Number(accounts[0].id) : 0);
		if (method.dateLocked) setPaymentDate(hoyCdmx());
		setRefCheck(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [methodId, methods]);

	useEffect(() => {
		if (!ticket) return;
		http.paymentIntentsService.voucher(campusId, Number(ticket.id))
			.then((r: any) => setAi({ loading: false, prefill: r.data?.prefill || null, warnings: r.data?.warnings || [], readings: r.data?.readings || [] }))
			.catch(() => setAi({ loading: false, prefill: null, warnings: [], readings: [] }));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
	/* Lo leído llena solo lo que el ticket o el borrador no traen; la cajera siempre puede corregir. */
	useEffect(() => {
		if (!ai || ai.loading || !ai.prefill || aiApplied.current || methods.length < 1 || draft) return;
		aiApplied.current = true;
		const p = ai.prefill;
		if (!ticket.amount && p.amount) setTotal(String(p.amount));
		if (!ticket.paidAt && p.paymentDate) setPaymentDate(p.paymentDate);
		if (!ticket.reference && p.reference) setReference(p.reference);
		const okMethod = p.paymentMethodId && methods.some((m: any) => Number(m.id) === Number(p.paymentMethodId));
		if (!ticket.paymentMethodId && okMethod) {
			if (Number(p.paymentMethodId) === methodId) {
				if (!ticket.recipientAccountId && p.accountId && accounts.some((a: any) => Number(a.id) === Number(p.accountId))) setAccountId(Number(p.accountId));
			} else {
				if (!ticket.recipientAccountId && p.accountId) pendingAccount.current = Number(p.accountId);
				setMethodId(Number(p.paymentMethodId));
			}
		} else if (!ticket.recipientAccountId && p.accountId && accounts.some((a: any) => Number(a.id) === Number(p.accountId))) {
			setAccountId(Number(p.accountId));
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ai, methods]);

	// Vista previa del comprobante del ticket, a la izquierda (nunca otra ventana).
	const showDoc = async (index: number) => {
		try {
			const r = await http.paymentIntentsService.downloadDocument(campusId, Number(ticket.id), index);
			const blob: Blob = r.data;
			if (preview?.url) URL.revokeObjectURL(preview.url);
			setPreview({ url: URL.createObjectURL(blob), type: blob.type || "", name: ticketDocs.find((d: any) => d.index === index)?.name || "Comprobante", index });
		} catch (e) {
			setPreview({ url: null, type: "", name: "", index });
		}
	};
	useEffect(() => {
		if (ticketDocs.length) showDoc(ticketDocs[0].index);
		return () => { if (preview?.url) URL.revokeObjectURL(preview.url); };
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const added = useRef<Set<number>>(new Set());
	// Promos flexibles: cada 30 s se vuelve a pintar para que la promo que llegó a cero desaparezca sola de la lista.
	const [, setReloj] = useState<number>(0);
	useEffect(() => { const t = setInterval(() => setReloj((x) => x + 1), 30000); return () => clearInterval(t); }, []);
	// «Aprobar de todos modos» un multimes vencido (Supervisión de Caja; ESPEC_AUDITORIA_PREGUNTAS B). Un envío a la vez (doble toque).
	const [excepcion, setExcepcion] = useState<{ agreementId: number; reason: string } | null>(null);
	const [excBusy, setExcBusy] = useState<boolean>(false);
	const excEnviando = useRef<boolean>(false);
	const aprobarExcepcion = async (student: RegisterStudent, agreementId: number) => {
		if (!excepcion || excEnviando.current) return;
		excEnviando.current = true;
		setExcBusy(true);
		try {
			const r = await http.paymentService.approveAdvanceException(campusId, agreementId, excepcion.reason.trim(), "payments");
			setExcepcion(null);
			setError(null);
			setNotice(r.data?.text || R.exceptionDone);
			const keep = persons.find((x) => x.student.id === student.id)?.lines || null;
			const loaded = await loadPerson(student, keep);
			setPersons((prev) => prev.map((x) => x.student.id === student.id ? { ...x, ...loaded, loading: false } : x));
		} catch (e) {
			setError(serverError(e));
		} finally {
			excEnviando.current = false;
			setExcBusy(false);
		}
	};
	const loadPerson = async (student: RegisterStudent, keepLines?: Record<string, Line> | null) => {
		const r = await http.paymentService.registerOwed(campusId, student.id, intentId);
		const d = r.data;
		const lines: Record<string, Line> = {};
		(d.rows as Row[]).forEach((row) => {
			const prev = keepLines ? keepLines[row.key] : undefined;
			lines[row.key] = prev ? { ...prev } : { on: false, apply: "", touched: false, promotionId: null, drId: null, note: "" };
			if (!prev && row.discountRequest && row.discountRequest.statusId === DR_ACEPTADO) lines[row.key].drId = row.discountRequest.id;
		});
		return { rows: d.rows as Row[], warnings: d.warnings || [], promos: (d.promotions || []).map((x: Promo) => ({ ...x, offsetMs: x.serverNowIso ? Date.parse(x.serverNowIso) - Date.now() : 0 })), lines, canWaive: !!d.canWaive, advances: (d.advances || []) as Advance[], blocked: (d.blockedAdvances || []) as BlockedAdvance[], student: { ...student, name: d.student?.name || nameOf(student), identityCode: d.student?.identityCode || student.identityCode }, campusNames: d.student?.campusNames || [] };
	};
	const addPerson = async (student: RegisterStudent, fromDraft?: any) => {
		if (!student || added.current.has(student.id)) return;
		added.current.add(student.id);
		setSearchOpen(false); setQuery(""); setResults([]);
		setPersons((prev) => [...prev, { student, campusNames: [], loading: true, rows: [], warnings: [], promos: [], lines: {}, notes: "", advances: [] }]);
		try {
			const saved = fromDraft && Array.isArray(fromDraft.persons) ? fromDraft.persons.find((p: any) => p.student?.id === student.id) : null;
			const loaded = await loadPerson(student, saved ? saved.lines : null);
			setPersons((prev) => prev.map((p) => p.student.id === student.id ? { ...p, ...loaded, loading: false, notes: saved?.notes || "" } : p));
		} catch (e) {
			setError(serverError(e));
			setPersons((prev) => prev.map((p) => p.student.id === student.id ? { ...p, loading: false } : p));
		}
	};
	const refreshPerson = async (studentId: number) => {
		const p = persons.find((x) => x.student.id === studentId);
		if (!p) return;
		try {
			const loaded = await loadPerson(p.student, p.lines);
			// Un especial que ya respondió: aceptado se usa; rechazado se quita.
			Object.keys(loaded.lines).forEach((k) => {
				const row = loaded.rows.find((x) => x.key === k);
				const l = loaded.lines[k];
				if (row?.discountRequest?.statusId === DR_ACEPTADO && !l.promotionId) l.drId = row.discountRequest.id;
				if (l.drId && (!row?.discountRequest || row.discountRequest.id !== l.drId || row.discountRequest.statusId === DR_RECHAZADO)) l.drId = null;
			});
			setPersons((prev) => prev.map((x) => x.student.id === studentId ? { ...x, ...loaded, notes: x.notes } : x));
		} catch (e) {
			setError(serverError(e));
		}
	};
	const removePerson = (id: number) => { added.current.delete(id); setPersons((prev) => prev.filter((p) => p.student.id !== id)); };

	const timer = useRef<any>(null);
	const search = (q: string) => {
		setQuery(q);
		clearTimeout(timer.current);
		if (q.trim().length < 2) { setResults([]); return; }
		timer.current = setTimeout(async () => {
			try {
				setSearching(true);
				const r = await http.paymentService.registerPeople(campusId, q.trim());
				setResults(r.data || []);
			} catch (e) {
				setError(serverError(e));
			} finally {
				setSearching(false);
			}
		}, 300);
	};

	// ------------------------------------------------------------------ montos

	const reqOf = (row: Row, l: Line): DReq | null => (l.drId && row.discountRequest && row.discountRequest.id === l.drId ? row.discountRequest : null);
	const discountOf = (p: Person, row: Row, l: Line): number => {
		if (l.promotionId) {
			const promo = p.promos.find((x) => x.id === l.promotionId);
			return promo ? discountFrom(row.listAmount, promo.calcType, promo.value) : 0;
		}
		const dr = reqOf(row, l);
		if (dr && dr.statusId === DR_ACEPTADO) return discountFrom(row.listAmount, dr.calcType, dr.value);
		return -1; // sin descuento nuevo: se queda el precio que ya trae el renglón
	};
	/*
		R4 (Esteban, 2026-10-08): la fecha que cuenta para el recargo es la FECHA DE PAGO del comprobante. Si la fecha es el día
		del vencimiento o antes y el pago liquida la mensualidad, el recargo que ya se generó se anula solo y la mensualidad
		conserva su descuento. Aquí solo se enseña; el servidor lo decide y lo deja en el historial y en Auditoría.
	*/
	const aTiempo = (row: Row): boolean => row.typeId === 1 && !!row.surcharge && !!row.surcharge.onTimeCancellable
		&& !!row.surcharge.dueDate && !!paymentDate && paymentDate <= row.surcharge.dueDate;
	const pendienteATiempo = (row: Row): number => round2(row.subTotal - row.paidAmount);
	/** El recargo de una mensualidad que se paga con comprobante a tiempo: no se cobra (se anula al registrar). */
	const recargoAnulado = (p: Person, row: Row): boolean => {
		if (row.typeId !== 2 || !row.parentKey) return false;
		const parent = p.rows.find((x) => x.key === row.parentKey);
		return !!parent && aTiempo(parent);
	};
	const dueOf = (p: Person, row: Row, l: Line): number => {
		if (recargoAnulado(p, row)) return 0;
		if (aTiempo(row)) return pendienteATiempo(row);
		const d = discountOf(p, row, l);
		return d < 0 ? row.pending : round2(row.listAmount - d - row.paidAmount);
	};
	const applyOf = (p: Person, row: Row, l: Line): number => (!l.on || recargoAnulado(p, row) ? 0 : (l.touched ? round2(Number(l.apply) || 0) : dueOf(p, row, l)));
	const stateOf = (p: Person, row: Row, l: Line): "full" | "partial" | "zero" | "none" => {
		const due = dueOf(p, row, l);
		const a = applyOf(p, row, l);
		if (due <= 0.009) return "zero";
		if (a >= due - 0.009) return "full";
		return a > 0.009 ? "partial" : "none";
	};

	/*
		A2' (Esteban, 2026-10-08): un mes regalado de un acuerdo pendiente de pago adelantado se cobra a precio normal (se marca
		y se aplica como cualquier otro). Queda en $0 saldado SOLO cuando el servidor registra el pago adelantado completo (lo
		que falte de las mensualidades que pagan, de una vez) en esa misma transacción. Aquí no se bloquea nada.
	*/
	const regaloOk = (_list: Person[], _row: Row): boolean => true;
	const esRegalo = (row: Row) => !!row.agreement && row.agreement.state === "regalo" && !row.agreement.conditionMet;
	const ajustarRegalos = (list: Person[]): Person[] => list;
	/** Marca completas, de una vez, las mensualidades que faltan del pago adelantado (el servidor salda los regalos al registrar). */
	const marcarAdelanto = (studentId: number, adv: Advance) => {
		setPersons((prev) => prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const lines = { ...p.lines };
			adv.keys.forEach((k) => { if (lines[k]) lines[k] = { ...lines[k], on: true, touched: false, apply: "" }; });
			return { ...p, lines };
		}));
	};

	const received = round2(Number(total) || 0);
	const applied = round2(persons.reduce((acc, p) => acc + p.rows.reduce((s, row) => s + applyOf(p, row, p.lines[row.key] || ({} as Line)), 0), 0));
	const left = round2(received - applied);

	// Lo sugerido: solo lo que el monto cubre COMPLETO, en orden (ticket, vencido, por fecha). Se detiene en el primero que no cabe.
	const suggest = (list: Person[], amount: number): Person[] => {
		let restante = round2(amount);
		let stop = false;
		const order: { p: number; row: Row }[] = [];
		list.forEach((p, pi) => p.rows.forEach((row) => order.push({ p: pi, row })));
		order.sort((a, b) => (a.p - b.p) || (Number(b.row.isTicket) - Number(a.row.isTicket)) || (Number(b.row.overdue) - Number(a.row.overdue))
			|| String(a.row.dueDate || "9999").localeCompare(String(b.row.dueDate || "9999")) || (a.row.kind === b.row.kind ? 0 : a.row.kind === "tuition" ? -1 : 1)
			|| ((a.row.monthIndex || 0) - (b.row.monthIndex || 0)) || ((a.row.typeId ?? 9) - (b.row.typeId ?? 9)));
		const next = list.map((p) => ({ ...p, lines: Object.fromEntries(Object.entries(p.lines).map(([k, l]) => [k, { ...l, on: false, touched: false, apply: "" }])) as Record<string, Line> }));
		order.forEach(({ p, row }) => {
			if (stop) return;
			const person = next[p];
			const l = person.lines[row.key];
			const due = dueOf(person, row, l);
			if (due <= 0.009) return;
			if (due <= restante + 0.009) {
				l.on = true;
				restante = round2(restante - due);
			} else {
				stop = true;
			}
		});
		// Un renglón en $0 (inscripción gratis) va con su matrícula cuando algo de ella va marcado.
		next.forEach((person) => person.rows.forEach((row) => {
			const l = person.lines[row.key];
			if (!esRegalo(row) && !recargoAnulado(person, row) && dueOf(person, row, l) <= 0.009 && person.rows.some((o) => o.enrollmentId === row.enrollmentId && o.key !== row.key && person.lines[o.key].on)) l.on = true;
		}));
		return ajustarRegalos(next);
	};
	// Lo que debe cada renglón (cambia con una promo o un especial aceptado): si cambia, se vuelve a sugerir.
	const dueSig = persons.map((p) => p.loading ? "x" : p.rows.map((row) => p.lines[row.key] ? dueOf(p, row, p.lines[row.key]) : 0).join(",")).join("|");
	useEffect(() => {
		if (manual || persons.some((p) => p.loading)) return;
		setPersons((prev) => suggest(prev, received));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [received, persons.length, dueSig]);

	const setLine = (studentId: number, key: string, fn: (l: Line, row: Row, p: Person) => Line) => {
		setManual(true);
		setPersons((prev) => prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const row = p.rows.find((r) => r.key === key)!;
			return { ...p, lines: { ...p.lines, [key]: fn(p.lines[key], row, p) } };
		}));
	};

	/** Marcar un renglón marca también los renglones en $0 de su matrícula (la inscripción gratis va con su mes). */
	const toggleRow = (studentId: number, key: string, on: boolean) => {
		setManual(true);
		setPersons((prev) => ajustarRegalos(prev.map((p) => {
			if (p.student.id !== studentId) return p;
			const row = p.rows.find((r) => r.key === key)!;
			const lines = { ...p.lines, [key]: { ...p.lines[key], on, touched: false, apply: "" } };
			// El recargo va con su mensualidad: se marca y desmarca con ella (la cajera puede desmarcar solo el recargo).
			if (row.surchargeKey && lines[row.surchargeKey]) lines[row.surchargeKey] = { ...lines[row.surchargeKey], on: on && !aTiempo(row), touched: false, apply: "" };
			if (on && row.enrollmentId) {
				p.rows.forEach((o) => { if (!esRegalo(o) && !recargoAnulado(p, o) && o.enrollmentId === row.enrollmentId && o.key !== key && dueOf(p, o, lines[o.key]) <= 0.009) lines[o.key] = { ...lines[o.key], on: true }; });
			}
			return { ...p, lines };
		})));
	};

	// ------------------------------------------------------------------ referencia

	const checkReference = async () => {
		const ref = reference.trim();
		if (!method || !method.referenceRequired || ref.length < 4 || !accountId) { setRefCheck(null); return; }
		try {
			const r = await http.paymentService.checkReference(campusId, { accountId, paymentMethodId: methodId, reference: ref, paymentDate, total: received });
			setRefCheck(r.data);
		} catch (e) {
			setRefCheck(null);
		}
	};
	useEffect(() => { checkReference(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [accountId, methodId, paymentDate, received]);

	// ------------------------------------------------------------------ especial

	const unaVez = useUnaALaVez();
	const sendSpecial = () => unaVez(sendSpecialAhora);
	const sendSpecialAhora = async () => {
		if (!special) return;
		try {
			setBusy(true);
			setError(null);
			const r = await http.paymentService.requestDiscount(campusId, { studentId: special.studentId, rowKey: special.key, calcType: special.calcType,
				value: Number(special.value), reason: special.reason.trim(), intentId });
			const dr: DReq = r.data;
			setPersons((prev) => prev.map((p) => p.student.id !== special.studentId ? p : {
				...p, rows: p.rows.map((row) => row.key === special.key ? { ...row, discountRequest: dr } : row),
				lines: { ...p.lines, [special.key]: { ...p.lines[special.key], promotionId: null, drId: dr.id, touched: false } }
			}));
			setSpecial(null);
			setNotice(R.specialSent);
			if (intentId) saveDraft(true);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	// ------------------------------------------------------------------ eximir recargo

	const sendWaiver = () => unaVez(sendWaiverAhora);
	const sendWaiverAhora = async () => {
		if (!waiver) return;
		try {
			setBusy(true);
			setError(null);
			const payload = { calcType: waiver.calcType, value: Number(waiver.value), reason: waiver.reason.trim() };
			if (waiver.mode === "waive") {
				await http.paymentService.waiveSurcharge(campusId, waiver.surchargeId, payload);
				setNotice(R.waived);
			} else {
				await http.paymentService.requestSurchargeWaiver(campusId, waiver.surchargeId, { ...payload, intentId });
				setNotice(R.waiverSent);
			}
			const sid = waiver.studentId;
			setWaiver(null);
			await refreshPerson(sid);
		} catch (e) {
			setError(serverError(e));
		} finally {
			setBusy(false);
		}
	};

	// ------------------------------------------------------------------ borrador (ticket)

	const draftPayload = () => ({ total, methodId, accountId, paymentDate, reference, payerName, payerPhone, notes,
		persons: persons.map((p) => ({ student: { id: p.student.id, name: nameOf(p.student), identityCode: p.student.identityCode }, notes: p.notes,
			lines: Object.fromEntries(Object.entries(p.lines).filter(([, l]) => l.on || l.promotionId || l.drId)) })) });
	const saveDraft = async (silent?: boolean) => {
		if (!intentId) return;
		try {
			await http.paymentIntentsService.saveDraft(campusId, intentId, draftPayload());
			if (!silent) setNotice(R.draftSaved);
		} catch (e) {
			setError(serverError(e));
		}
	};

	// ------------------------------------------------------------------ registrar

	const payPersons = persons.filter((p) => p.rows.some((row) => p.lines[row.key]?.on));
	const ticketSinAlumno = !!ticket && !(Number(ticket.studentId) > 0);
	const ownerId: number = ticketSinAlumno ? (payPersons.some((p) => p.student.id === owner) ? owner : (payPersons[0]?.student.id || 0)) : 0;
	const onLines = persons.flatMap((p) => p.rows.filter((row) => p.lines[row.key]?.on).map((row) => ({ p, row, l: p.lines[row.key] })));
	const pendingSpecial = onLines.some(({ row, l }) => { const dr = reqOf(row, l); return !!dr && dr.statusId !== DR_ACEPTADO; });
	const filesNeeded = !!method?.attachmentRequired && files.length + ticketDocs.length < 1;
	const whyNot: string | null =
		received <= 0 || onLines.length < 1 ? R.why.rows
			: Math.abs(left) >= 0.005 ? R.why.left
				: !methodId || !accountId ? R.why.method
					: method?.referenceRequired && reference.trim().length < 4 ? R.why.reference
						: refCheck && !refCheck.ok ? (refCheck.message || R.why.reference)
							: notes.trim().length < 10 ? R.why.notes
								: filesNeeded ? R.why.files
									: pendingSpecial ? R.why.pending

										//Ticket sin alumno en WAPP (/pagar): Caja elige a la persona y el servidor liga el ticket al dueño del comprobante (intentStudentId).
										: ticket && !(Number(ticket.studentId) > 0) && persons.length === 0 ? R.why.ticketNoStudent
										: ticket && Number(ticket.studentId) > 0 && !persons.some((p) => p.student.id === Number(ticket.studentId)) ? R.why.ticketStudent
											: null;

	const submit = () => unaVez(submitAhora);
	const submitAhora = async () => {
		if (whyNot || busy) return;
		try {
			setBusy(true);
			setError(null);
			const payload = {
				total: received, paymentMethodId: methodId, accountId, paymentDate, reference: method?.referenceRequired ? reference.trim() : "",
				notes: notes.trim(), payerName: payerName.trim(), payerPhone: payerPhone.trim(), clientKey: clientKey.current, intentId,
				...(ownerId ? { intentStudentId: ownerId } : {}),
				attachedFiles: files,
				persons: payPersons.map((p) => ({
					studentId: p.student.id, notes: p.notes.trim(),
					rows: p.rows.filter((row) => p.lines[row.key]?.on && !recargoAnulado(p, row)).map((row) => {
						const l = p.lines[row.key];
						const dr = reqOf(row, l);
						return { key: row.key, apply: applyOf(p, row, l), promotionId: l.promotionId || null,
							discountRequestId: !l.promotionId && dr && dr.statusId === DR_ACEPTADO ? dr.id : null, discountNote: l.note.trim() };
					})
				}))
			};
			const r = await http.paymentService.register(campusId, payload);
			setResult(r.data);
		} catch (e) {
			setError(serverError(e));
			clientKey.current = newKey();
		} finally {
			setBusy(false);
		}
	};

	const openReceipt = (id: number) => abrirArchivo({ nombre: `Recibo del pago ${id}.pdf`, cargar: () => http.fileService.getReceiptPDF(campusId, id).then((r: any) => new Blob([r.data], { type: "application/pdf" })) });
	const finish = () => { if (result) onSuccess(result); else onClose(); };

	// ------------------------------------------------------------------ vista

	const lab = (t: string) => <span style={{ fontSize: 12, fontWeight: 700, color: "var(--wa-apagado)", display: "block", marginBottom: 3 }}>{t}</span>;
	const pill = (t: string, tone: "oro" | "acento" | "filo") => (
		<span style={{ fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 999, whiteSpace: "nowrap", marginLeft: 6,
			background: tone === "oro" ? "var(--wa-oro-suave)" : tone === "acento" ? "var(--wa-acento-suave)" : "var(--wa-papel)",
			color: tone === "oro" ? "var(--wa-oro)" : tone === "acento" ? "var(--wa-acento-texto)" : "var(--wa-apagado)",
			border: tone === "filo" ? "1px solid var(--wa-filo)" : "none" }}>{t}</span>
	);
	const stateTag = (s: string) => s === "full" ? pill(R.states.full, "acento") : s === "partial" ? pill(R.states.partial, "oro") : s === "zero" ? pill(R.states.zero, "filo") : s === "none" ? pill(R.states.none, "oro") : null;

	const blockPago = (
		<div>
			<h3 style={{ fontSize: 16, margin: "0 0 8px" }}>{R.paymentBlock} <small style={{ fontWeight: 500, color: "var(--wa-apagado)", fontSize: 13 }}>{R.paymentBlockHint}</small></h3>
			<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "repeat(3, minmax(0,1fr))", gap: 10 }}>
				<label style={LB}>{lab(R.received + " (" + currencySymbol + ")")}
					<input className="form-control" type="number" min="0" step="0.01" value={total} disabled={busy} style={{ fontWeight: 700 }}
						onChange={(e) => setTotal(e.target.value)} /></label>
				<label style={LB}>{lab(R.date)}
					<input className="form-control" type="date" value={paymentDate} max={hoyCdmx()} disabled={busy || !!method?.dateLocked} onChange={(e) => setPaymentDate(e.target.value)} /></label>
				<label style={LB}>{lab(R.method)}
					<select className="form-select" value={methodId} disabled={busy} onChange={(e) => setMethodId(Number(e.target.value))}>
						{methods.map((m: any) => <option key={"m" + m.id} value={m.id}>{m.name}</option>)}
					</select></label>
				<label style={LB}>{lab(R.account)}
					<select className="form-select" value={accountId} disabled={busy || accounts.length < 1} onChange={(e) => setAccountId(Number(e.target.value))}>
						{accounts.length !== 1 ? <option value={0}>—</option> : ""}
						{accounts.map((a: any) => <option key={"a" + a.id} value={a.id}>{a.name}</option>)}
					</select></label>
				{method?.referenceRequired ? (
					<label style={LB}>{lab(R.reference)}
						<input className="form-control" value={reference} disabled={busy} onChange={(e) => { setReference(e.target.value); setRefCheck(null); }} onBlur={checkReference} />
					</label>
				) : ""}
				<label style={LB}>{lab(R.payer)}<input className="form-control" value={payerName} disabled={busy} onChange={(e) => setPayerName(e.target.value)} /></label>
				<label style={LB}>{lab(R.payerPhone)}<input className="form-control" value={payerPhone} disabled={busy} onChange={(e) => setPayerPhone(e.target.value)} /></label>
				<label style={{ ...LB, gridColumn: movil ? "1 / -1" : "span 2" }}>{lab(R.notes)}
					<input className="form-control" value={notes} disabled={busy} onChange={(e) => setNotes(e.target.value)} /></label>
				<label style={{ ...LB, gridColumn: movil ? "1 / -1" : undefined }}>{lab(R.files + (ticketDocs.length ? " · " + R.ticketFiles(ticketDocs.length) : ""))}
					{/* Botón propio en español (el del navegador decía «Choose Files / No file chosen»). */}
					<span style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
						<span className={"btn btn-outline-primary btn-sm rounded-pill" + (busy ? " disabled" : "")} style={{ minHeight: 40, display: "inline-flex", alignItems: "center" }}>Elegir Archivos</span>
						<span style={{ fontSize: 13, color: "var(--wa-apagado)", minWidth: 0, overflowWrap: "anywhere" }}>
							{files.length ? files.map((f) => f.name).join(", ") : "Ningún archivo elegido"}</span>
					</span>
					<input type="file" multiple accept="image/png,image/jpeg,image/webp,application/pdf" disabled={busy} style={{ display: "none" }}
						onChange={(e) => setFiles(Array.from(e.target.files || []))} /></label>
			</div>
			{method?.referenceRequired && refCheck ? (
				<div style={{ fontSize: 13, fontWeight: 600, marginTop: 6, color: !refCheck.ok || refCheck.repeated ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>
					{!refCheck.ok || refCheck.repeated ? refCheck.message : "✓ " + R.refOk}
				</div>
			) : ""}
		</div>
	);

	const rowCells = (p: Person, row: Row): React.ReactNode => {
		if (row.parentKey && p.rows.some((x) => x.key === row.parentKey)) return null;   // va dentro de su mensualidad
		const child = row.surchargeKey ? p.rows.find((x) => x.key === row.surchargeKey) || null : null;
		const box = special && special.studentId === p.student.id && special.key === row.key ? specialBoxFn()
			: waiver && waiver.studentId === p.student.id && (waiver.key === row.key || (child && waiver.key === child.key)) ? waiverBoxFn() : null;
		const main = rowMain(p, row, child);
		if (!box) return main;
		return movil ? <React.Fragment key={row.key}>{main}{box}</React.Fragment>
			: <React.Fragment key={row.key}>{main}<tr><td></td><td colSpan={6} style={{ paddingBottom: 8 }}>{box}</td></tr></React.Fragment>;
	};
	/** Lo del recargo dentro del renglón: "+ recargo 300", días, próximo aumento, eximir/sugerir, sugerencia pendiente. */
	const surchargeInfo = (p: Person, s: Row): React.ReactNode => {
		const info = s.surcharge || {};
		const wr = info.waiverRequest;
		const canAct = !!info.canWaiveNow && !(wr && wr.statusId === DR_PENDIENTE);
		return (
			<div style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600, marginTop: 2 }}>
				{R.surchargeLine(info.days)}{info.waived > 0 ? " · " + R.surchargeWaived(money(info.waived, currencySymbol)) : ""}
				{info.next ? <span style={{ color: "var(--wa-apagado)", fontWeight: 500 }}> · {R.surchargeNext(money(info.next.amount, currencySymbol), fecha(info.next.date))}</span> : null}
				{info.state === "detenido" ? <span style={{ color: "var(--wa-apagado)", fontWeight: 500 }}> · {R.surchargeStopped}</span> : null}
				{wr && wr.statusId === DR_PENDIENTE ? <span> · {R.waiverPending}</span> : null}
				{wr && wr.statusId === DR_RECHAZADO ? <span style={{ fontWeight: 500 }}> · {R.waiverRejected(wr.decisionNotes || "")}</span> : null}
				{canAct ? <> · <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12, verticalAlign: "baseline" }} disabled={busy}
					onClick={() => setWaiver({ studentId: p.student.id, key: s.key, surchargeId: Number(info.surchargeId), mode: p.canWaive ? "waive" : "request", calcType: "percent", value: "", reason: "", pending: s.pending })}>
					{p.canWaive ? R.waive : R.suggestWaive}</button></> : null}
			</div>
		);
	};

	const rowMain = (p: Person, row: Row, child?: Row | null) => {
		const l = p.lines[row.key];
		const due = dueOf(p, row, l);
		const s = l.on ? stateOf(p, row, l) : "";
		const options = promosFor(row, p.promos);
		const dr = row.discountRequest;
		const selectValue = l.promotionId ? "p" + l.promotionId : (l.drId ? "d" + l.drId : "");
		const drShown = dr && (dr.statusId === DR_PENDIENTE || dr.statusId === DR_ACEPTADO);
		const descuento = row.canDiscount ? (
			<select className="form-select form-select-sm" value={selectValue} disabled={busy} style={{ textOverflow: "ellipsis" }}
				title={l.promotionId ? (p.promos.find((x) => x.id === l.promotionId)?.conditions || "") : (row.agreementLock ? R.agreementLocked : "")}
				onChange={(e) => {
					const v = e.target.value;
					if (v === "esp") { setSpecial({ studentId: p.student.id, key: row.key, calcType: "percent", value: "", reason: "" }); return; }
					setLine(p.student.id, row.key, (x) => ({ ...x, promotionId: v.startsWith("p") ? Number(v.slice(1)) : null, drId: v.startsWith("d") ? Number(v.slice(1)) : null, touched: false }));
				}}>
				<option value="">{row.agreementLock ? R.agreementLocked : row.currentDiscount > 0 ? "Ya trae " + money(row.currentDiscount, currencySymbol) : R.noDiscount}</option>
				{options.map((o) => <option key={"o" + o.id} value={"p" + o.id}>{o.name}{o.countdownToIso ? " · " + restante(Date.parse(o.countdownToIso) - (Date.now() + (o.offsetMs || 0))).toLowerCase() : ""}{o.quota?.text ? " · " + o.quota.text : ""}</option>)}
				{drShown ? <option value={"d" + dr!.id}>{dr!.statusId === DR_ACEPTADO ? R.specialAccepted(money(dr!.discountAmount, currencySymbol)) : R.specialPending}</option> : null}
				<option value="esp">{R.special}</option>
			</select>
		) : <span style={{ color: "var(--wa-apagado)", fontSize: row.discountBlocked && row.typeId === 1 ? 12 : undefined }}>{row.discountBlocked && row.typeId === 1 ? R.lateNoDiscount : (row.currentDiscount > 0 ? money(row.currentDiscount, currencySymbol) : "—")}</span>;
		const drNote = dr && l.drId === dr.id && dr.statusId === DR_PENDIENTE ? <div style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600 }}>{R.specialPending} · <button type="button" className="btn btn-link btn-sm p-0" style={{ fontSize: 12 }} onClick={() => refreshPerson(p.student.id)}>{R.specialRefresh}</button></div>
			: dr && dr.statusId === DR_RECHAZADO ? <div style={{ fontSize: 12, color: "var(--wa-oro)" }}>{R.specialRejected(dr.decisionNotes || "")}</div> : null;
		const nota = (l.promotionId || l.drId) ? <input className="form-control form-control-sm" value={l.note} disabled={busy} placeholder={R.cols.note} onChange={(e) => setLine(p.student.id, row.key, (x) => ({ ...x, note: e.target.value }))} /> : <span style={{ color: "var(--wa-tenue)" }}>—</span>;
		const seAplica = (
			<input className="form-control form-control-sm" type="number" min="0" step="0.01" style={{ textAlign: "right", fontWeight: 700, maxWidth: 120, marginLeft: "auto" }}
				value={!l.on ? "" : (l.touched ? l.apply : String(due))} disabled={busy || !l.on || due <= 0.009}
				onChange={(e) => setLine(p.student.id, row.key, (x) => ({ ...x, apply: e.target.value, touched: true }))} />
		);
		const concepto = (
			<div>
				<span style={{ fontWeight: 600 }}>{row.title}</span>
				{row.overdue ? pill(R.overdue, "oro") : null}
				{row.invoiceId ? pill(R.invoicePill(row.invoiceId), "filo") : null}
				<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>
					{[cursoCorto(row.courseName), row.scheduleName ? horarioBonito(row.scheduleName, { plural: true, separador: " " }) : "", row.monthIndex && row.monthsTotal ? `mes ${row.monthIndex} de ${row.monthsTotal}` : "", row.campusId !== campusId ? row.campusName : ""].filter(Boolean).join(" · ")}
				</div>
				{drNote}
				{row.agreement ? (
					<div style={{ fontSize: 12, fontWeight: 600, color: esRegalo(row) ? "var(--wa-oro)" : "var(--wa-acento-texto)" }}>
						{R.agreement(row.agreement.name)} · {esRegalo(row)
							? R.giftLocked(row.agreement.conditionText || "")
							: row.agreement.state === "gratis" || row.agreement.state === "regalo" ? R.giftReady
								: row.agreement.state === "lista" ? R.agreementList
									: R.agreementPrice(money(row.subTotal, currencySymbol), money(row.listAmount, currencySymbol))}
					</div>
				) : row.agreementLock ? <div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{R.agreement(row.agreementLock)}</div> : null}
				{aTiempo(row) ? <div style={{ fontSize: 12, color: "var(--wa-acento-texto)", fontWeight: 600 }}>{R.onTime(fecha(paymentDate), fecha(row.surcharge.dueDate), money(pendienteATiempo(row), currencySymbol))}</div> : null}
				{row.typeId === 2 && !child ? surchargeInfo(p, row) : null}
				{child && recargoAnulado(p, child) ? (
					<div style={{ fontSize: 12, color: "var(--wa-apagado)", marginTop: 4 }}><span style={{ textDecoration: "line-through" }}>+ {R.surcharge} {money(child.pending, currencySymbol)}</span> · {R.onTimeSurcharge}</div>
				) : child ? (
					<div style={{ display: "flex", gap: 6, alignItems: "flex-start", marginTop: 4 }}>
						<input type="checkbox" checked={!!p.lines[child.key]?.on} disabled={busy} aria-label={child.title}
							onChange={(e) => setLine(p.student.id, child.key, (x) => ({ ...x, on: e.target.checked, touched: false, apply: "" }))} />
						<div><span style={{ fontWeight: 600 }}>+ {R.surcharge}</span>{surchargeInfo(p, child)}</div>
					</div>
				) : null}
			</div>
		);
		const cl = child ? p.lines[child.key] : null;
		const childDue = child && cl ? dueOf(p, child, cl) : 0;
		const childState = child && cl && cl.on ? stateOf(p, child, cl) : "";
		const childMonto = child && !recargoAnulado(p, child) ? <div style={{ color: "var(--wa-oro)", fontWeight: 600 }}>+ {money(childDue, currencySymbol)}</div> : null;
		const childApply = child && cl && !recargoAnulado(p, child) ? (
			<input className="form-control form-control-sm" type="number" min="0" step="0.01" aria-label={R.surcharge}
				style={{ textAlign: "right", fontWeight: 700, maxWidth: 120, marginLeft: "auto", marginTop: 4 }}
				value={!cl.on ? "" : (cl.touched ? cl.apply : String(childDue))} disabled={busy || !cl.on || childDue <= 0.009}
				onChange={(e) => setLine(p.student.id, child.key, (x) => ({ ...x, apply: e.target.value, touched: true }))} />
		) : null;
		const monto = (
			<div style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
				{discountOf(p, row, l) >= 0 && l.on ? <div style={{ textDecoration: "line-through", color: "var(--wa-apagado)", fontSize: 12 }}>{money(row.pending, currencySymbol)}</div> : null}
				<div>{money(due, currencySymbol)}</div>
				{childMonto}
			</div>
		);
		const check = <input type="checkbox" checked={l.on} disabled={busy} aria-label={row.title}
			onChange={(e) => toggleRow(p.student.id, row.key, e.target.checked)} />;
		if (movil) {
			return (
				<div key={row.key} style={{ borderTop: "1px solid var(--wa-filo)", padding: "8px 0", opacity: l.on ? 1 : 0.85 }}>
					<div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>{check}<div className="rpFila" style={{ flex: 1 }} onClick={(e) => {
						// Tocar el renglón marca/desmarca (en el teléfono la casilla sola es un blanco chico); no si se tocó un control.
						if (busy || (e.target as HTMLElement).closest("input,button,select,textarea,a")) return;
						toggleRow(p.student.id, row.key, !l.on);
					}}>{concepto}<div style={{ fontSize: 12, color: "var(--wa-apagado)" }}>{R.cols.due} {fecha(row.dueDate)}</div></div>{monto}</div>
					<div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 6, marginTop: 6, paddingLeft: 32 }}>{descuento}<div>{seAplica}{childApply}</div></div>
					{l.promotionId || l.drId ? <div style={{ marginTop: 6, paddingLeft: 32 }}>{nota}</div> : null}
					{s || childState ? <div style={{ textAlign: "right", marginTop: 4 }}>{s ? stateTag(s) : null}{childState ? stateTag(childState) : null}</div> : null}
				</div>
			);
		}
		return (
			<tr key={row.key} style={{ opacity: l.on ? 1 : 0.85, borderTop: "1px solid var(--wa-filo)", verticalAlign: "top" }}>
				<td style={{ width: 28, paddingTop: 8 }}>{check}</td>
				<td style={{ padding: "6px 0" }}>{concepto}</td>
				<td style={{ whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums", padding: "0 8px" }}>{fecha(row.dueDate)}</td>
				<td style={{ whiteSpace: "nowrap", paddingRight: 8 }}>{monto}</td>
				<td style={{ width: 200 }}>{descuento}{l.promotionId || l.drId ? <div style={{ marginTop: 4 }}>{nota}</div> : null}</td>
				<td style={{ width: 130 }}>{seAplica}{childApply}</td>
				<td style={{ width: 96, textAlign: "right" }}>{s ? stateTag(s) : null}{childState ? <div style={{ marginTop: 8 }}>{stateTag(childState)}</div> : null}</td>
			</tr>
		);
	};

	const specialBoxFn = () => special ? (() => {
		const p = persons.find((x) => x.student.id === special.studentId);
		const row = p?.rows.find((x) => x.key === special.key);
		if (!p || !row) return null;
		const preview2 = Number(special.value) > 0 ? discountFrom(row.listAmount, special.calcType, Number(special.value)) : 0;
		return (
			<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: 10, marginTop: 8 }}>
				<b>{R.specialTitle}</b> · {row.title} · lista {money(row.listAmount, currencySymbol)}
				<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "110px 120px 1fr auto auto", gap: 8, marginTop: 8, alignItems: "end" }}>
					<select className="form-select form-select-sm" value={special.calcType} onChange={(e) => setSpecial({ ...special, calcType: e.target.value as any })}>
						<option value="percent">{R.specialPercent}</option><option value="amount">{R.specialAmount}</option>
					</select>
					<input className="form-control form-control-sm" type="number" min="0" step="0.01" value={special.value} onChange={(e) => setSpecial({ ...special, value: e.target.value })} />
					<input className="form-control form-control-sm" placeholder={R.specialReason} value={special.reason} onChange={(e) => setSpecial({ ...special, reason: e.target.value })} style={{ gridColumn: movil ? "1 / -1" : undefined }} />
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setSpecial(null)}>{R.cancel}</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || !(Number(special.value) > 0) || special.reason.trim().length < 10} onClick={sendSpecial}>{R.specialSend}</button>
				</div>
				{preview2 > 0 ? <div style={{ fontSize: 12, marginTop: 4 }}>− {money(preview2, currencySymbol)} → {money(row.listAmount - preview2, currencySymbol)}</div> : null}
			</div>
		);
	})() : null;

	const waiverBoxFn = () => waiver ? (() => {
		const amount = Number(waiver.value) > 0 ? (waiver.calcType === "percent" ? round2(waiver.pending * Math.min(100, Number(waiver.value)) / 100) : Math.min(waiver.pending, round2(Number(waiver.value)))) : 0;
		return (
			<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: 10, marginTop: 8 }}>
				<b>{waiver.mode === "waive" ? R.waiveTitle : R.suggestWaiveTitle}</b> · {R.surchargeOwes(money(waiver.pending, currencySymbol))}
				<div style={{ display: "grid", gridTemplateColumns: movil ? "1fr 1fr" : "110px 120px 1fr auto auto", gap: 8, marginTop: 8, alignItems: "end" }}>
					<select className="form-select form-select-sm" value={waiver.calcType} onChange={(e) => setWaiver({ ...waiver, calcType: e.target.value as any })}>
						<option value="percent">{R.specialPercent}</option><option value="amount">{R.specialAmount}</option>
					</select>
					<input className="form-control form-control-sm" type="number" min="0" step="0.01" value={waiver.value} onChange={(e) => setWaiver({ ...waiver, value: e.target.value })} />
					<input className="form-control form-control-sm" placeholder={R.waiveReason} value={waiver.reason} onChange={(e) => setWaiver({ ...waiver, reason: e.target.value })} style={{ gridColumn: movil ? "1 / -1" : undefined }} />
					<button type="button" className="btn btn-outline-secondary btn-sm" disabled={busy} onClick={() => setWaiver(null)}>{R.cancel}</button>
					<button type="button" className="btn btn-primary btn-sm" disabled={busy || !(amount > 0) || waiver.reason.trim().length < 10} onClick={sendWaiver}>{waiver.mode === "waive" ? R.waiveSend : R.specialSend}</button>
				</div>
				{amount > 0 ? <div style={{ fontSize: 12, marginTop: 4 }}>− {money(amount, currencySymbol)} → {money(Math.max(0, waiver.pending - amount), currencySymbol)}{amount >= waiver.pending - 0.009 ? " · " + R.waiveTotal : ""}</div> : null}
			</div>
		);
	})() : null;

	const blockAplica = (
		<div style={{ marginTop: 18 }}>
			<div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
				<h3 style={{ fontSize: 16, margin: 0 }}>{R.applyBlock} <small style={{ fontWeight: 500, color: "var(--wa-apagado)", fontSize: 13 }}>{R.applyBlockHint}</small></h3>
				{manual ? <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => { setManual(false); setPersons((prev) => suggest(prev, received)); }}>{R.suggestAgain}</button> : null}
			</div>
			{persons.map((p) => (
				<div key={"p" + p.student.id} style={{ border: "1px solid var(--wa-filo)", marginTop: 10 }}>
					<div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "8px 12px", background: "var(--wa-papel)", flexWrap: "wrap" }}>
						<div><b style={{ fontSize: 15 }}>{nameOf(p.student)}</b> <span style={{ color: "var(--wa-apagado)", fontSize: 13 }}>· {p.student.identityCode || ""}{p.campusNames.length ? " · " + p.campusNames.join(", ") : ""}</span></div>
						{ticket && p.student.id === Number(ticket.studentId) ? null : <button type="button" className="btn btn-link btn-sm p-0" disabled={busy} onClick={() => removePerson(p.student.id)}>{R.remove}</button>}
					</div>
					<div style={{ padding: "0 12px 8px" }}>
						{p.warnings.map((w, i) => <div key={"w" + i} style={{ color: "var(--wa-oro)", fontSize: 13, fontWeight: 600, paddingTop: 6 }}>{w}</div>)}
						{(p.blocked || []).filter((b) => b.canApprove).map((b) => (
							<div key={"bl" + b.agreementId} style={{ border: "1px solid var(--wa-oro)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
								{excepcion && excepcion.agreementId === b.agreementId ? (
									<>
										<b>{R.exceptionTitle(b.name)}</b>
										<div style={{ color: "var(--wa-apagado)", margin: "2px 0 6px" }}>{R.exceptionHelp}</div>
										<textarea className="form-control" rows={2} style={{ fontSize: 16 }} value={excepcion.reason} disabled={excBusy} placeholder={R.exceptionReason}
											onChange={(e) => setExcepcion({ agreementId: b.agreementId, reason: e.target.value })} />
										<div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
											<button type="button" className="btn btn-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={excBusy || excepcion.reason.trim().length < 10}
												onClick={() => aprobarExcepcion(p.student, b.agreementId)}>{excBusy ? "Enviando…" : R.exceptionConfirm}</button>
											<button type="button" className="btn btn-link btn-sm" style={{ minHeight: 40 }} disabled={excBusy} onClick={() => setExcepcion(null)}>{R.exceptionCancel}</button>
										</div>
									</>
								) : (
									<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ minHeight: 40 }} disabled={busy}
										onClick={() => setExcepcion({ agreementId: b.agreementId, reason: "" })}>{R.exceptionButton(b.name)}</button>
								)}
							</div>
						))}
						{(p.advances || []).map((adv) => {
							const faltan = adv.keys.filter((k) => !(p.lines[k] && p.lines[k].on && stateOf(p, p.rows.find((x) => x.key === k) as Row, p.lines[k]) === "full")).length;
							const gifts = adv.gifts.filter((g) => g.statusId !== 2);
							const giftText = adv.giftStudentId !== p.student.id ? R.advanceGiftOther(persons.find((x) => x.student.id === adv.giftStudentId) ? nameOf(persons.find((x) => x.student.id === adv.giftStudentId)!.student) : "otra persona")
								: gifts.map((g) => g.title).join(", ");
							return (
								<div key={"adv" + adv.agreementId} style={{ border: "1px dashed var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 10px", marginTop: 8, fontSize: 13 }}>
									<b>{R.advanceTitle(adv.name)}</b> · {R.advanceText(money(adv.amount, currencySymbol), adv.pendingCount)} {gifts.length || adv.giftStudentId !== p.student.id ? R.advanceGifts(giftText) : ""}
									<div style={{ color: "var(--wa-apagado)", marginTop: 2 }}>{R.advanceNormal}{adv.text ? " " + adv.text : ""}</div>
									{faltan > 0 && adv.keys.length > 0 ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 6 }} disabled={busy} onClick={() => marcarAdelanto(p.student.id, adv)}>{R.advanceMark}</button> : null}
								</div>
							);
						})}
						{p.loading ? <div style={{ color: "var(--wa-apagado)", padding: 8 }}>{R.loading}</div>
							: p.rows.length < 1 ? <div style={{ color: "var(--wa-oro)", padding: 8, fontWeight: 600 }}>{R.nothing}</div>
								: movil ? <div>{p.rows.map((row) => rowCells(p, row))}</div>
									: (
										<table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }} className="waRegistrarTabla">
											<thead><tr style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--wa-apagado)" }}>
												<th></th><th>{R.cols.concept}</th><th>{R.cols.due}</th><th style={{ textAlign: "right" }}>{R.cols.amount}</th><th style={{ paddingLeft: 8 }}>{R.cols.discount}</th><th style={{ textAlign: "right" }}>{R.cols.apply}</th><th></th>
											</tr></thead>
											<tbody>{p.rows.map((row) => rowCells(p, row))}</tbody>
										</table>
									)}
					</div>
				</div>
			))}
			<div style={{ marginTop: 10 }}>
				{!searchOpen ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => setSearchOpen(true)}>{R.addPerson}</button> : (
					<div style={{ position: "relative", maxWidth: 520 }}>
						<input className="form-control" autoFocus value={query} placeholder={R.searchPlaceholder} onChange={(e) => search(e.target.value)} />
						{query.trim().length >= 2 ? (
							<div style={{ background: "var(--wa-superficie)", border: "1px solid var(--wa-filo)", borderTop: 0, maxHeight: 260, overflow: "auto" }}>
								{searching ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>…</div>
									: results.length < 1 ? <div style={{ padding: 8, color: "var(--wa-apagado)" }}>{R.noResults}</div>
										: results.map((s: any) => {
											const dup = persons.some((p) => p.student.id === s.id);
											return (
												<div key={"r" + s.id} style={{ padding: "6px 10px", cursor: dup ? "default" : "pointer", opacity: dup ? 0.5 : 1, borderBottom: "1px solid var(--wa-filo)" }}
													onClick={() => !dup && addPerson({ id: s.id, name: s.name, identityCode: s.identityCode })}>
													<b>{s.name}</b> <span style={{ color: "var(--wa-apagado)", fontSize: 12 }}>· {s.identityCode || ""} · {s.campusName}{dup ? " · " + R.alreadyIn : ""}</span>
												</div>
											);
										})}
							</div>
						) : null}
					</div>
				)}
			</div>
		</div>
	);

	const comprobante = ticket ? (
		<aside style={{ background: "var(--wa-papel)", border: "1px solid var(--wa-filo)", padding: 12, alignSelf: "start" }}>
			<div style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, color: "var(--wa-apagado)" }}>{R.files}</div>
			{ticketDocs.length > 1 ? (
				<div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "6px 0" }}>
					{ticketDocs.map((d: any) => <button key={"d" + d.index} type="button" className={"btn btn-sm " + (preview?.index === d.index ? "btn-primary" : "btn-outline-secondary")} onClick={() => showDoc(d.index)}>{d.name}</button>)}
				</div>
			) : null}
			{preview && preview.url ? (
				<div style={{ marginTop: 6, cursor: "zoom-in" }} onClick={() => abrirArchivo({ nombre: preview.name, cargar: () => http.paymentIntentsService.downloadDocument(campusId, Number(ticket.id), preview.index).then((r: any) => r.data) })}>
					{preview.type.startsWith("image/") ? <img src={preview.url} alt={preview.name} style={{ width: "100%", maxHeight: movil ? 260 : 520, objectFit: "contain", background: "var(--wa-superficie)" }} />
						: preview.type === "application/pdf" && !movil ? <iframe title={preview.name} src={preview.url} style={{ width: "100%", height: 520, border: 0 }} />
							: <div style={{ padding: 12 }}>{preview.name}</div>}
				</div>
			) : <div style={{ color: "var(--wa-apagado)", padding: 8 }}>{ticketDocs.length ? "…" : "—"}</div>}
			<div style={{ fontSize: 13, marginTop: 10, display: "grid", gap: 4 }}>
				{ai && ai.loading ? <div style={{ color: "var(--wa-apagado)" }}>{R.aiReading}</div> : null}
				{ai && !ai.loading && ai.readings.length > 0 ? (
					<div style={{ borderBottom: "1px dashed var(--wa-filo)", paddingBottom: 6 }}>
						<span style={{ fontSize: 11, letterSpacing: ".08em", textTransform: "uppercase", fontWeight: 700, color: "var(--wa-apagado)" }}>{R.aiRead}</span>
						{ai.readings.map((x: any, k: number) => <div key={"ar" + k}>{x.summary}</div>)}
					</div>
				) : null}
				{ticket.amount ? <div><span style={{ color: "var(--wa-apagado)" }}>Monto Reportado </span><b>{money(ticket.amount, currencySymbol)}</b></div> : null}
				{ticket.reference ? <div><span style={{ color: "var(--wa-apagado)" }}>Referencia </span><b>{ticket.reference}</b></div> : null}
				{ticket.paidAt ? <div><span style={{ color: "var(--wa-apagado)" }}>Fecha </span><b>{fecha(ticket.paidAt)}</b></div> : null}
				{ticket.notes ? <div style={{ whiteSpace: "pre-wrap" }}><span style={{ color: "var(--wa-apagado)" }}>Nota </span>{String(ticket.notes).split("\n")[0]}</div> : null}
			</div>
			{ticketSinAlumno && payPersons.length > 1 ? (
				<label style={{ ...LB, marginTop: 10 }}>{lab(R.owner)}
					<select className="form-select form-select-sm" value={ownerId} disabled={busy} onChange={(e) => setOwner(Number(e.target.value))}>
						{payPersons.map((p) => <option key={"o" + p.student.id} value={p.student.id}>{nameOf(p.student)}</option>)}
					</select>
				</label>
			) : null}
		</aside>
	) : null;

	const resultView = result ? (
		<div style={{ border: "1px solid var(--wa-acento)", background: "var(--wa-acento-suave)", padding: 14 }}>
			<h3 style={{ fontSize: 18, margin: "0 0 8px" }}>{R.done(result.id)}</h3>
			{(result.movements || []).map((m: any) => (
				<div key={"m" + m.id} style={{ padding: "4px 0" }}>
					<b>{m.label}</b> · {m.studentName}: {(m.concepts || []).map((c: any) => `${c.title} ${R.states2[c.state] || ""}${c.state === "abono" ? " " + money(c.applied, currencySymbol) : ""}`).join(" · ")}
					<span style={{ color: "var(--wa-apagado)" }}> · factura {m.invoiceId}</span>
				</div>
			))}
			{(result.fulfilled || []).map((f: any) => <div key={"f" + f.agreementId} style={{ color: "var(--wa-acento-texto)", fontWeight: 600, marginTop: 6 }}>{R.fulfilled(f.name, f.saldados)}</div>)}
			{(result.audits || []).length ? <div style={{ color: "var(--wa-oro)", fontWeight: 600, marginTop: 6 }}>{result.audits.map((a: any) => a.details).join(" ")} · Auditoría</div> : null}
			<button type="button" className="btn btn-outline-primary btn-sm rounded-pill" style={{ marginTop: 10 }} onClick={() => openReceipt(result.id)}>{R.receipt}</button>
		</div>
	) : null;

	return (
		<Modal className={"waModulo" + (movil ? " rpMovil" : "")} show onHide={busy ? undefined : finish} size="xl" centered scrollable fullscreen="lg-down" enforceFocus={false}>
			{/* Teléfono (Esteban, 2026-10-09): casillas y botones del tamaño de un dedo (44 px), no de un cursor. */}
			{movil ? <style>{".rpMovil input[type=checkbox]{width:24px;height:24px;flex:0 0 24px;margin:0}"
				+ ".rpMovil .modal-footer .btn,.rpMovil .modal-body .btn-sm{min-height:44px;padding-left:16px;padding-right:16px}"
				+ ".rpMovil .rpFila{cursor:pointer;min-height:44px}"}</style> : null}
			<Modal.Header closeButton={!busy}>
				<Modal.Title style={{ fontSize: 18 }}>
					{ticket ? R.ticket(Number(ticket.id)) + " · " : ""}{R.title}
				</Modal.Title>
			</Modal.Header>
			<Modal.Body style={{ textAlign: "left", fontSize: 14 }}>
				{error ? <div className="alert alert-danger" style={{ fontSize: 14 }}>{error}</div> : null}
				{notice && !result ? <div className="alert alert-warning" style={{ fontSize: 14 }}>{notice}</div> : null}
				{!result && ai && ai.warnings.length > 0 ? (
					<div style={{ border: "1px solid var(--wa-oro)", background: "var(--wa-oro-suave)", padding: "8px 12px", marginBottom: 12 }}>
						<b style={{ color: "var(--wa-oro)" }}>{R.review}</b>
						{ai.warnings.map((w, k) => <div key={"aw" + k} style={{ fontSize: 14 }}>{w}</div>)}
					</div>
				) : null}
				{result ? resultView : (
					<div style={{ display: "grid", gridTemplateColumns: ticket && !movil ? "minmax(0, 0.8fr) minmax(0, 2.2fr)" : "1fr", gap: 16 }}>
						{comprobante}
						<div>{blockPago}{blockAplica}</div>
					</div>
				)}
			</Modal.Body>
			<Modal.Footer style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 8 }}>
				{result ? <div /> : (
					<div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontVariantNumeric: "tabular-nums" }}>
						<span>{R.totals.received} <b style={{ fontSize: 17 }}>{money(received, currencySymbol)}</b></span>
						<span>{R.totals.applied} <b style={{ fontSize: 17 }}>{money(applied, currencySymbol)}</b></span>
						<span style={{ color: Math.abs(left) >= 0.005 ? "var(--wa-oro)" : undefined }}>{R.totals.left} <b style={{ fontSize: 17 }}>{left < 0 ? money(-left, currencySymbol) + " " + R.over : money(left, currencySymbol)}</b></span>
					</div>
				)}
				<div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "flex-end" }}>
					{!result && whyNot && onLines.length > 0 ? <span style={{ fontSize: 12, color: "var(--wa-oro)", fontWeight: 600 }}>{whyNot}</span> : null}
					{result ? <button type="button" className="btn btn-primary btn-sm rounded-pill" onClick={finish}>{R.close}</button> : (
						<>
							<button type="button" className="btn btn-outline-secondary btn-sm rounded-pill" disabled={busy} onClick={onClose}>{R.cancel}</button>
							{intentId ? <button type="button" className="btn btn-outline-primary btn-sm rounded-pill" disabled={busy} onClick={() => saveDraft(false)}>{R.saveDraft}</button> : null}
							<button type="button" className="btn btn-primary btn-sm rounded-pill" disabled={!!whyNot || busy} onClick={submit}>{busy ? "…" : R.register}</button>
						</>
					)}
				</div>
			</Modal.Footer>
		</Modal>
	);
};

export default RegisterPaymentModal;
